import { useState, type ReactNode } from 'react';
import type { WizardOptionCopy } from '@flashpoint/site-dna';

interface OptionCardProps {
  copy: WizardOptionCopy;
  selected: boolean;
  onSelect: () => void;
  /** Top-left badge content. Usually a glyph, shell number, or pictogram. */
  badge?: ReactNode;
  /** Optional top-right meta strip (e.g. "Auto pick" / "density · busy"). */
  meta?: ReactNode;
  /** Override how many bestFor pills show in the collapsed state (default 3). */
  collapsedPills?: number;
  /** Optional extra body content rendered above the description. */
  extraBody?: ReactNode;
  /** Optional unique key for accessible labeling - useful when many cards share copy.name. */
  ariaId?: string;
}

/**
 * The shared option-card UI used across every content-shaping wizard step.
 *
 * Always-visible: badge, name, oneLiner, top N "Best for" pills.
 * Expandable: clientPitch (italic, read-aloud), full bestFor list,
 * avoidIf (red-pill chips), feel adjectives (monospace strip).
 *
 * Read along with packages/site-dna/src/copy/* for the source-of-truth
 * strings. The card itself is presentation only.
 */
export function OptionCard({
  copy,
  selected,
  onSelect,
  badge,
  meta,
  collapsedPills = 3,
  extraBody,
  ariaId,
}: OptionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const visiblePills = copy.bestFor.slice(0, collapsedPills);
  const hasMore = copy.bestFor.length > collapsedPills || copy.avoidIf.length > 0;

  const onToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  return (
    <div
      className={`overflow-hidden rounded-xl border text-left transition ${
        selected
          ? 'border-flashpoint-ember ring-2 ring-flashpoint-ember'
          : 'border-nm-border hover:border-nm-muted'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        aria-describedby={ariaId ? `${ariaId}-pitch` : undefined}
        className="w-full text-left"
      >
        <div className="flex items-start gap-3 border-b border-nm-border bg-nm-moss/40 p-3">
          {badge ? (
            <div className="shrink-0 rounded-lg bg-nm-chrome px-2.5 py-2 font-mono text-[10px] uppercase tracking-widest text-nm-ink">
              {badge}
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-nm-ink">{copy.name}</div>
            <div className="mt-1 text-xs text-nm-ink">{copy.oneLiner}</div>
          </div>
          {meta ? (
            <div className="shrink-0 text-right text-[10px] uppercase tracking-widest text-nm-muted">
              {meta}
            </div>
          ) : null}
        </div>

        <div className="space-y-2 p-3">
          {extraBody}
          {visiblePills.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-widest text-nm-muted">
                Best for
              </span>
              {visiblePills.map((p) => (
                <span
                  key={p}
                  className="rounded-full bg-nm-chrome px-2 py-0.5 text-[11px] text-nm-ink"
                >
                  {p}
                </span>
              ))}
              {copy.bestFor.length > collapsedPills && (
                <span className="text-[11px] text-nm-muted">
                  +{copy.bestFor.length - collapsedPills}
                </span>
              )}
            </div>
          )}
        </div>
      </button>

      {hasMore && (
        <div className="border-t border-nm-border/60">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="w-full px-3 py-2 text-left text-[11px] font-mono uppercase tracking-widest text-nm-muted hover:text-nm-ink"
          >
            {expanded ? '▾  Hide pitch' : '▸  Read pitch'}
          </button>

          {expanded && (
            <div id={ariaId ? `${ariaId}-pitch` : undefined} className="space-y-3 px-3 pb-3">
              <p className="border-l-2 border-flashpoint-ember/60 pl-3 text-sm italic text-nm-ink">
                {copy.clientPitch}
              </p>

              {copy.bestFor.length > collapsedPills && (
                <div>
                  <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-nm-muted">
                    Best for (full)
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {copy.bestFor.map((p) => (
                      <span
                        key={p}
                        className="rounded-full bg-nm-chrome px-2 py-0.5 text-[11px] text-nm-ink"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {copy.avoidIf.length > 0 && (
                <div>
                  <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-nm-danger/70">
                    Avoid if
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {copy.avoidIf.map((p) => (
                      <span
                        key={p}
                        className="rounded-full bg-nm-danger/15 px-2 py-0.5 text-[11px] text-nm-danger"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {copy.feel.length > 0 && (
                <div className="font-mono text-[11px] uppercase tracking-widest text-nm-muted">
                  feel · {copy.feel.join(' · ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
